import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Icon } from "../components/Icon";
import { VoiceButton } from "./VoiceInput";
import { parseQuery, intentWhen } from "../utils/queryParse";
import { fillText } from "../utils/leadDisplay";
import { tripWhen } from "./home/HomeAtoms";
import { CAR_TYPES, FUELS, CAR_LANGS, seatsValue, optionLabel } from "../utils/listChoices";
import { tripText } from "../data/seasonTrips";
import { localCity } from "../utils/localNames";
import { track } from "../utils/analytics";

/* The search history is recorded by the lists (utils/searchLog.js), which know what
   each search found. The tracker that used to live here never recorded anything: it
   passed "POST" where apiRequest takes an options object, so every call was a GET
   with no body, and 404'd. */

/* The searches made on this phone, for the box's own list (mock Four_1 change 5): kept
   on the phone only - never sent with an account - eight at most, the newest first. */
const RECENT_MAX = 8;
const recentKey = (page) => `sramika_recent_q_${page}`;
const readRecent = (page) => { try { return JSON.parse(localStorage.getItem(recentKey(page)) || "[]").filter((x) => typeof x === "string"); } catch { return []; } };
const writeRecent = (page, list) => { try { localStorage.setItem(recentKey(page), JSON.stringify(list.slice(0, RECENT_MAX))); } catch { /* storage blocked */ } };
export function rememberSearch(page, q) {
  const v = String(q || "").trim().replace(/\s+/g, " ");
  if (v.length < 2) return;
  writeRecent(page, [v, ...readRecent(page).filter((x) => x.toLowerCase() !== v.toLowerCase())]);
}

const SUGGESTIONS = { cars: ["sq_ex1", "sq_ex2", "sq_ex3", "sq_ex4"], drivers: ["sq_dx1", "sq_dx2", "sq_dx3", "sq_dx4"] };

/* The search box of both home lists, with Filters at its end: 46px, and the Filters
   button says how many filters are on - the number is what tells someone why the list
   looks short.
   Since 24 Oct 2026 (mock Four_1, changes 4-9): a 🎤 to speak, the last searches and
   ready questions when the empty box is tapped, and a sentence understood - "tomorrow
   7-seater to Tirupati" - shown as tags with one button to set that trip (onIntent). */
export default function SearchBar({
  onSearch,
  onFilterClick,
  initialValue = "",
  placeholder,
  filterCount = 0,
  page = "cars",
  onIntent,
  trips = [],
}) {
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(() => readRecent(page));
  const boxRef = useRef(null);
  const settle = useRef(null);

  // A search set from outside - a suggestion under "no results", or Show all - shows
  // in the box too, not only in the list.
  useEffect(() => { setQuery(initialValue || ""); }, [initialValue]);
  useEffect(() => () => clearTimeout(settle.current), []);

  const intent = useMemo(() => (onIntent ? parseQuery(query) : null), [query, onIntent]);

  function setText(val, { remember = false } = {}) {
    setQuery(val);
    onSearch?.(val);
    clearTimeout(settle.current);
    if (remember) { rememberSearch(page, val); setRecent(readRecent(page)); return; }
    // Kept once the words stand still, as the list logs them (utils/searchLog.js).
    settle.current = setTimeout(() => { rememberSearch(page, val); setRecent(readRecent(page)); }, 1500);
  }

  function handleChange(e) { setText(e.target.value); }

  function handleClear() {
    setQuery("");
    onSearch?.("");
    clearTimeout(settle.current);
  }

  const pick = (v, from) => { track("search_pick", { from, page }); setText(v, { remember: true }); setOpen(false); };
  const removeRecent = (v) => { const next = recent.filter((x) => x !== v); writeRecent(page, next); setRecent(next); };
  const clearRecent = () => { writeRecent(page, []); setRecent([]); };

  // The understood request, as tags: what, where, when.
  const tags = intent ? [
    intent.seats && page === "cars" ? fillText(t("hm_nSeats"), { n: intent.seats }) : "",
    intent.carType && page === "cars" ? t((CAR_TYPES.find((o) => o.value === intent.carType) || {}).key || "") : "",
    intent.fuel && page === "cars" ? t((FUELS.find((o) => o.value === intent.fuel) || {}).key || "") : "",
    intent.language ? fillText(t("sq_speaks"), { lang: optionLabel(CAR_LANGS.find((o) => o.value === intent.language) || { value: intent.language, text: intent.language }, t, lang) }) : "",
    intent.trip ? `${tripText(intent.trip.dest || intent.trip.name, lang)} · ${t(intent.tripType === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip")}`
      : intent.city ? `${localCity(intent.city, lang)}${intent.tripType ? ` · ${t(intent.tripType === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip")}` : ""}` : "",
    tripWhen(intentWhen(intent), t, lang),
  ].filter(Boolean) : [];
  const goLabel = intent && (intent.trip || intent.city) ? t("sq_goTrip") : t(page === "drivers" ? "sq_goDrivers" : "sq_goCars");
  const applyIntent = () => {
    track("search_understood", { page, place: intent.trip?.slug || intent.city || null, tags: tags.length });
    rememberSearch(page, query); setRecent(readRecent(page));
    setQuery(""); onSearch?.("");
    onIntent({ ...intent, when: intentWhen(intent), seatsValue: intent.seats ? seatsValue(intent.seats) : "" });
  };

  const showPanel = open && !query.trim();

  /* The box and Filters side by side, as tall as the From / To boxes above (50px, 40 on a
     phone - mock Fix_A change 7) - the box is marked .ctl so it
     renders at the four-fifths height the Filters button does (index.css). */
  return (
    <div className="relative" ref={boxRef}>
    <div className="flex items-stretch gap-2">
      <div className="ctl flex-1 min-w-0 flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-[14px] pl-3 pr-1 h-[50px] focus-within:border-[#7BAE00] shadow-[0_2px_10px_rgba(15,23,42,0.04)] transition-colors">
        <span className="text-[#94A3B8] shrink-0" aria-hidden="true"><Icon name="search" size={16} strokeWidth={2.2} /></span>

        <input
          id="cab-search-bar"
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder || t("hm_searchCars")}
          aria-label={placeholder || t("hm_searchCars")}
          className="flex-1 min-w-0 text-[11px] text-[#1F2937] placeholder:text-[#98A2B3] outline-none focus:ring-0 focus:outline-none border-none bg-transparent"
        />

        {query ? (
          <button type="button" onClick={handleClear} aria-label={t("hm_clearSearch")} title={t("hm_clearSearch")}
            className="shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center text-[#6B7280] active:bg-[#F1F5F9]">
            <Icon name="x" size={15} strokeWidth={2.4} />
          </button>
        ) : (
          <VoiceButton where={`search_${page}`} className="w-[36px] h-[36px]" onText={(v) => setText(v, { remember: true })} />
        )}
      </div>

      {/* Filters as a square icon button, its count on its corner (mock Home_1 change 5). */}
      <button type="button" onClick={onFilterClick} aria-label={t("hm_filters")} title={t("hm_filters")}
        className={`relative shrink-0 h-[50px] aspect-square rounded-[14px] lg:hidden flex items-center justify-center shadow-[0_2px_10px_rgba(15,23,42,0.04)] active:scale-[0.97] transition-colors ${
          filterCount > 0 ? "bg-[#7BAE00] text-white" : "bg-white text-[#334155] border border-[#E5E7EB]"}`}>
        <Icon name="filter" size={17} strokeWidth={2.2} />
        {filterCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0F172A] text-white text-[9.5px] font-extrabold flex items-center justify-center leading-none">
            {filterCount}
          </span>
        )}
      </button>
    </div>

      {/* Tapping the empty box: the last searches on this phone, and questions to try. */}
      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-30 rounded-2xl bg-white border border-[#E5E7EB] shadow-[0_12px_32px_rgba(15,23,42,0.14)] p-3"
          onMouseDown={(e) => e.preventDefault()}>
          {recent.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-extrabold text-[#64748B]">{t("sq_recent")}</p>
                <button type="button" onClick={clearRecent} className="text-[10px] font-bold text-[#5E8700] min-h-[30px] px-1">{t("sq_clear")}</button>
              </div>
              {recent.slice(0, 5).map((r) => (
                <div key={r} className="flex items-center gap-2 border-b border-[#F1F5F9] last:border-0">
                  <button type="button" onClick={() => pick(r, "recent")} className="flex-1 min-w-0 min-h-[40px] flex items-center gap-2.5 text-left text-[12px] text-[#1F2937]">
                    <Icon name="clock" size={14} stroke="#94A3B8" strokeWidth={2.2} /><span className="truncate">{r}</span>
                  </button>
                  <button type="button" onClick={() => removeRecent(r)} aria-label={t("sq_remove")} title={t("sq_remove")}
                    className="shrink-0 w-[32px] h-[32px] rounded-full flex items-center justify-center text-[#94A3B8] active:bg-[#F1F5F9]">
                    <Icon name="x" size={13} strokeWidth={2.4} />
                  </button>
                </div>
              ))}
            </>
          )}
          {onIntent && (
            <>
              <p className={`text-[10px] font-extrabold text-[#64748B] ${recent.length ? "mt-2.5" : ""}`}>{t("sq_try")}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {SUGGESTIONS[page].map((k) => (
                  <button key={k} type="button" onClick={() => pick(t(k), "example")}
                    className="min-h-[34px] px-3 rounded-full border border-[#E5E7EB] bg-white text-[11px] font-semibold text-[#334155] active:bg-[#F1F7E3]">
                    {t(k)}
                  </button>
                ))}
              </div>
            </>
          )}
          {trips.length > 0 && (
            <>
              <p className="text-[10px] font-extrabold text-[#64748B] mt-2.5">{t("sq_trips")}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {trips.slice(0, 4).map((tr) => (
                  <button key={tr.key} type="button" onClick={() => { setOpen(false); tr.onClick(); }}
                    className="min-h-[34px] px-3 rounded-full border border-[#E5E7EB] bg-white text-[11px] font-semibold text-[#334155] active:bg-[#F1F7E3]">
                    {tr.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* A sentence understood (mock Four_1 change 7): what, where and when as tags, and
          one button - set the trip, or show the cars / drivers asked for. */}
      {intent && tags.length > 0 && (
        <div className="mt-2 rounded-[14px] bg-[#F4F9EA] border border-[#DDEBC2] px-3 py-2.5">
          <p className="text-[10.5px] font-extrabold text-[#2F4700]">{t("sq_understood")}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {tags.map((tag) => (
              <span key={tag} className="rounded-lg bg-white border border-[#D6E6B0] px-2 py-[3px] text-[10.5px] font-bold text-[#3F5E00]">{tag}</span>
            ))}
          </div>
          <button type="button" onClick={applyIntent}
            className="mt-2 w-full min-h-[42px] rounded-xl bg-[#7BAE00] text-white text-[11.5px] font-extrabold active:scale-[0.99]">
            {goLabel}
          </button>
        </div>
      )}
    </div>
  );
}
